import PropTypes from 'prop-types';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import Fill from 'ol/style/Fill';
import Circle from 'ol/style/Circle';
import Image from 'ol/style/Image';
clusterStackStyle.propTypes = {
  /** Feature style function for rendering each feature in stack, or single features. */
  featureStyle: PropTypes.func.isRequired,
  /** If set, one feature per distinct value in this field is rendered. */
  typeField: PropTypes.string,
  /** Maximum number of features to render in stack. */
  stackLimit: PropTypes.number,
  /** Horizontal offset of each stack item. */
  stackOffsetX: PropTypes.number,
  /** Vertical offset of each stack item. */
  stackOffsetY: PropTypes.number,
  /** Function generating the cluster label text. */
  clusterLabelText: PropTypes.func.isRequired,
  /** Horizontal offset position of cluster size label, in resolution units. */
  clusterLabelOffsetX: PropTypes.number,
  /** Vertical offset position of cluster size label, in resolution units. */
  clusterLabelOffsetY: PropTypes.number,
  /** Background image for cluster size label. Defaults to a white/gray circle. */
  clusterLabelBackgroundImage: PropTypes.instanceOf(Image),
  /** Callback that can process the feature array before rendering the stack (e.g. change order). */
  processFeatures: PropTypes.func
};
clusterStackStyle.defaultProps = {
  stackLimit: 5,
  stackOffsetX: -2,
  stackOffsetY: 3,
  clusterLabelOffsetX: 8,
  clusterLabelOffsetY: -5,
  clusterLabelBackgroundImage: new Circle()
};

/**
 * Renders clusters as a stack of features with a size label. Can be configured to render distinct features with typeField.
 *
 * @component
 */
export default function clusterStackStyle(_ref) {
  var featureStyle = _ref.featureStyle,
    typeField = _ref.typeField,
    _ref$stackLimit = _ref.stackLimit,
    stackLimit = _ref$stackLimit === void 0 ? 5 : _ref$stackLimit,
    _ref$stackOffsetX = _ref.stackOffsetX,
    stackOffsetX = _ref$stackOffsetX === void 0 ? -2 : _ref$stackOffsetX,
    _ref$stackOffsetY = _ref.stackOffsetY,
    stackOffsetY = _ref$stackOffsetY === void 0 ? 3 : _ref$stackOffsetY,
    processFeatures = _ref.processFeatures,
    clusterLabelText = _ref.clusterLabelText,
    _ref$clusterLabelOffs = _ref.clusterLabelOffsetX,
    clusterLabelOffsetX = _ref$clusterLabelOffs === void 0 ? 8 : _ref$clusterLabelOffs,
    _ref$clusterLabelOffs2 = _ref.clusterLabelOffsetY,
    clusterLabelOffsetY = _ref$clusterLabelOffs2 === void 0 ? -5 : _ref$clusterLabelOffs2,
    _ref$clusterLabelBack = _ref.clusterLabelBackgroundImage,
    clusterLabelBackgroundImage = _ref$clusterLabelBack === void 0 ? new Circle({
      radius: 7,
      fill: new Fill({
        color: '#fff'
      }),
      stroke: new Stroke({
        color: '#999'
      })
    }) : _ref$clusterLabelBack;
  return function (cluster, resolution) {
    var features = cluster.get('features');
    if (features.length === 0) return;
    if (features.length === 1) return featureStyle(features[0], resolution);
    var offsetGeometry = function offsetGeometry(x, y) {
      return function (f) {
        var geom = f.getGeometry().clone();
        geom.translate(x * resolution, y * resolution);
        return geom;
      };
    };
    var stackedTypes = [];
    var stackedFeatures = features;
    if (typeField) {
      stackedFeatures = [];
      features.forEach(function (f) {
        if (stackedTypes.indexOf(f.get(typeField)) === -1) {
          stackedTypes.push(f.get(typeField));
          stackedFeatures.push(f);
        }
      });
    }
    if (processFeatures) stackedFeatures = processFeatures(stackedFeatures);
    return [new Style({
      geometry: offsetGeometry(clusterLabelOffsetX, clusterLabelOffsetY),
      text: clusterLabelText(cluster, resolution),
      zIndex: 100
    }), new Style({
      geometry: offsetGeometry(clusterLabelOffsetX, clusterLabelOffsetY),
      image: clusterLabelBackgroundImage,
      zIndex: 99
    })].concat(stackedFeatures.slice(0, stackLimit).map(function (f, i) {
      var style = featureStyle(f, resolution);
      style.setGeometry(offsetGeometry(i * stackOffsetX, i * stackOffsetY));
      return style;
    })).reverse();
  };
}